import {
  Button,
  type DropTargetGetFeedback,
  Menu,
  Skeleton,
  Tooltip,
} from '@affine/component';
import { ResizePanel } from '@affine/component/resize-panel';
import { useAppSettingHelper } from '@affine/core/components/hooks/affine/use-app-setting-helper';
import { NavigateContext } from '@affine/core/components/hooks/use-navigate-helper';
import { useHoverSupport } from '@affine/core/components/hooks/use-hover-support';
// import { WorkspaceNavigator } from '@affine/core/components/workspace-selector';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useI18n } from '@affine/i18n';
import {
  useLiveData,
  useService,
  useServiceOptional,
  useServices,
} from '@toeverything/infra';
import clsx from 'clsx';
import type { PropsWithChildren, ReactElement } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { WorkbenchService } from '../../workbench';
import { allowedSplitViewEntityTypes } from '../../workbench/view/split-view/types';
import { WorkspaceService } from '../../workspace';
import { AppSidebarService } from '../services/app-sidebar';
import * as styles from './fallback.css';
import {
  hoverNavWrapperStyle,
  navBodyStyle,
  navHeaderStyle,
  navStyle,
  navWrapperStyle,
  resizeHandleShortcutStyle,
  sidebarFloatMaskStyle,
  simpleSidebarStyle,
  simpleSidebarBottom,
  simpleSidebarBottomAvatar,
  simpleSidebarBottomIcon,
  simpleSidebarHeaderIcon,
  simpleSidebarHeader,
  simpleSidebarBottomLogin,
  simpleSidebarHeaderIconWrapper,
  homeButtonIcon,
  needRightBorder,
  simpleSidebarBottomAiFree,
  simpleSidebarBottomAiFreeTitle,
  simpleSidebarBottomAiFreeValue,
  simpleSidebarBottomAiFreeButton,
  simpleSidebarBottomAiFreeButtonText,
} from './index.css';
import { SidebarHeader, SidebarSwitch } from './sidebar-header';
import IconFont from '@ys/repo-ui/base/IconFont';
import { AddPageButton } from './add-page-button';
import {
  AuthService,
  SubscriptionService,
  UserCopilotQuotaService,
} from '../../cloud';
import {
  Fallback as AvatarFallback,
  Image as AvatarImage,
  Root as AvatarRoot,
} from '@radix-ui/react-avatar';
import { AccountMenu } from '@affine/core/components/root-app-sidebar/user-info/account-menu';
import { WorkspaceDialogService } from '../../dialogs';

export type History = {
  stack: string[];
  current: number;
};

const MAX_WIDTH = typeof window !== 'undefined' ? window.innerWidth - 750 : 480;
const MIN_WIDTH = 250;
const isMacosDesktop = BUILD_CONFIG.isElectron && environment.isMacOs;

export function AppSidebar({ children }: PropsWithChildren) {
  const { appSettings } = useAppSettingHelper();

  const clientBorder = appSettings.clientBorder;

  const appSidebarService = useService(AppSidebarService).sidebar;
  const workbenchService = useService(WorkbenchService).workbench;
  // const view = useService(ViewService).view;
  // const location = useLiveData(view.location$);
  const activeView = useLiveData(workbenchService.activeView$);

  // 获取当前视图的位置信息
  const location = useLiveData(activeView?.location$);

  const open = useLiveData(appSidebarService.open$);
  const width = useLiveData(appSidebarService.width$);
  const smallScreenMode = useLiveData(appSidebarService.smallScreenMode$);
  const hovering = useLiveData(appSidebarService.hovering$) && open !== true;
  const resizing = useLiveData(appSidebarService.resizing$);
  const supportsHover = useHoverSupport();

  const sidebarState = smallScreenMode
    ? open
      ? 'floating-with-mask'
      : 'close'
    : open
      ? 'open'
      : hovering
        ? 'floating'
        : 'close';

  const hasRightBorder = !BUILD_CONFIG.isElectron && !clientBorder;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      appSidebarService.setOpen(open);
    },
    [appSidebarService]
  );

  const handleResizing = useCallback(
    (resizing: boolean) => {
      appSidebarService.setResizing(resizing);
    },
    [appSidebarService]
  );

  const handleWidthChange = useCallback(
    (width: number) => {
      appSidebarService.setWidth(width);
    },
    [appSidebarService]
  );
  const isHomePage = useMemo(() => {
    return location.pathname.split('/').includes('home');
  }, [location.pathname]);

  const handleClose = useCallback(() => {
    appSidebarService.setOpen(false);
  }, [appSidebarService]);

  useEffect(() => {
    if (sidebarState !== 'floating' || resizing) {
      return;
    }
    const onMouseMove = (e: MouseEvent) => {
      const menuElement = document.querySelector(
        'body > [data-radix-popper-content-wrapper] > [data-radix-menu-content]'
      );

      if (menuElement) {
        return;
      }

      if (e.clientX > width + 20) {
        appSidebarService.setHovering(false);
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
    };
  }, [appSidebarService, resizing, sidebarState, width]);
  useEffect(() => {
    const preloadImage = new Image();
    preloadImage.src =
      'https://wy-static.wenxiaobai.com/wenxiaobai-web/web-image/muset/sidebar-logo.png';
  }, []);

  const resizeHandleDropTargetOptions = useMemo(() => {
    return () => ({
      data: () => {
        const firstView = workbenchService.views$.value.at(0);

        if (!firstView) {
          return {};
        }

        return {
          at: 'workbench:resize-handle',
          edge: 'left', // left of the first view
          viewId: firstView.id,
        };
      },
      canDrop: (data: DropTargetGetFeedback<AffineDNDData>) => {
        return (
          (!!data.source.data.entity?.type &&
            allowedSplitViewEntityTypes.has(data.source.data.entity?.type)) ||
          data.source.data.from?.at === 'workbench:link'
        );
      },
    });
  }, [workbenchService.views$.value]);
  useEffect(() => {
    if (!supportsHover) {
      appSidebarService.setOpen(false);
    }
  }, [supportsHover, appSidebarService]);

  return (
    <>
      {sidebarState === 'close' || sidebarState === 'floating' ? (
        <>
          <SimpleSidebar isHomePage={isHomePage} />
          <ResizePanel
            resizeHandleDropTargetOptions={resizeHandleDropTargetOptions}
            floating={sidebarState === 'floating'}
            open={sidebarState !== 'close'}
            resizing={resizing}
            maxWidth={MAX_WIDTH}
            minWidth={MIN_WIDTH}
            width={width}
            resizeHandlePos="right"
            onOpen={handleOpenChange}
            onResizing={handleResizing}
            onWidthChange={handleWidthChange}
            unmountOnExit={false}
            className={clsx(navWrapperStyle, {
              [hoverNavWrapperStyle]: sidebarState === 'floating',
              [needRightBorder]: isHomePage,
            })}
            resizeHandleOffset={0}
            resizeHandleVerticalPadding={clientBorder ? 16 : 0}
            resizeHandleTooltip={<ResizeHandleTooltipContent />}
            resizeHandleTooltipOptions={{
              side: 'right',
              align: 'center',
            }}
            resizeHandleTooltipShortcut={['$mod', '/']}
            resizeHandleTooltipShortcutClassName={resizeHandleShortcutStyle}
            data-transparent
            data-open={true}
            data-has-border={hasRightBorder}
            data-testid="app-sidebar-wrapper"
            data-is-macos-electron={isMacosDesktop}
            data-client-border={clientBorder}
            data-is-electron={BUILD_CONFIG.isElectron}
            disableResize={false}
          >
            <nav className={navStyle} data-testid="app-sidebar">
              <div className={navBodyStyle} data-testid="sliderBar-inner">
                {sidebarState !== 'close' && children}
              </div>
            </nav>
          </ResizePanel>
        </>
      ) : (
        <ResizePanel
          resizeHandleDropTargetOptions={resizeHandleDropTargetOptions}
          floating={sidebarState === 'floating-with-mask'}
          open={true}
          resizing={resizing}
          maxWidth={MAX_WIDTH}
          minWidth={MIN_WIDTH}
          width={width}
          resizeHandlePos="right"
          onOpen={handleOpenChange}
          onResizing={handleResizing}
          onWidthChange={handleWidthChange}
          unmountOnExit={false}
          className={clsx(navWrapperStyle, {
            [needRightBorder]: isHomePage,
          })}
          resizeHandleOffset={0}
          resizeHandleVerticalPadding={clientBorder ? 16 : 0}
          resizeHandleTooltip={<ResizeHandleTooltipContent />}
          resizeHandleTooltipOptions={{
            side: 'right',
            align: 'center',
          }}
          resizeHandleTooltipShortcut={['$mod', '/']}
          resizeHandleTooltipShortcutClassName={resizeHandleShortcutStyle}
          data-transparent
          data-open={true}
          data-has-border={hasRightBorder}
          data-testid="app-sidebar-wrapper"
          data-is-macos-electron={isMacosDesktop}
          data-client-border={clientBorder}
          data-is-electron={BUILD_CONFIG.isElectron}
          disableResize={false}
        >
          <nav className={navStyle} data-testid="app-sidebar">
            {!BUILD_CONFIG.isElectron && <SidebarHeader />}
            <div className={navBodyStyle} data-testid="sliderBar-inner">
              {children}
            </div>
          </nav>
        </ResizePanel>
      )}

      <div
        data-testid="app-sidebar-float-mask"
        data-open={open}
        data-is-floating={sidebarState === 'floating-with-mask'}
        className={sidebarFloatMaskStyle}
        onClick={handleClose}
      />
    </>
  );
}

const ResizeHandleTooltipContent = () => {
  const t = useI18n();
  return (
    <div>
      <div>{t['com.affine.rootAppSidebar.resize-handle.tooltip.drag']()}</div>
      <div>{t['com.affine.rootAppSidebar.resize-handle.tooltip.click']()}</div>
    </div>
  );
};

const HomePageButton = () => {
  const { workbenchService, workspaceService } = useServices({
    WorkbenchService,
    WorkspaceService,
  });
  const workbench = workbenchService.workbench;
  const t = useI18n();
  const handleOpenHome = () => {
    workbench.openDoc('home');
    workspaceService.workspace.eventBus.emit(
      { id: 'chat.created', _type: 'chat.created' },
      {}
    );
  };

  return (
    <Tooltip content={t['com.affine.home']()} side="bottom" align="center">
      <IconFont
        className={homeButtonIcon}
        name="home"
        size={24}
        onClick={() => {
          handleOpenHome();
        }}
      />
    </Tooltip>
  );
};

const SimpleSidebar = ({ isHomePage }: { isHomePage: boolean }) => {
  const authService = useService(AuthService);
  const copilotQuotaService = useService(UserCopilotQuotaService);
  const subscriptionService = useService(SubscriptionService);
  const workspaceDialogService = useService(WorkspaceDialogService);
  const copilotActionLimit = useLiveData(
    copilotQuotaService.copilotQuota.copilotActionLimit$
  );
  const copilotActionUsed = useLiveData(
    copilotQuotaService.copilotQuota.copilotActionUsed$
  );
  const account = useLiveData(authService.session.account$);
  const loginStatus = useLiveData(authService.session.status$);
  const appSidebarService = useService(AppSidebarService).sidebar;
  const leftSidebarOpen = useLiveData(appSidebarService.open$);
  const copilotActionLeft = useMemo(() => {
    if (
      copilotActionLimit !== 'unlimited' &&
      copilotActionUsed !== null &&
      copilotActionLimit !== null
    ) {
      return copilotActionLimit - copilotActionUsed;
    }
    return 0;
  }, [copilotActionLimit, copilotActionUsed]);
  useEffect(() => {
    copilotQuotaService.copilotQuota.revalidate();
  }, [copilotQuotaService]);
  useEffect(() => {
    // revalidate subscription to get the latest status
    subscriptionService.subscription.revalidate();
  }, [subscriptionService]);
  const plan = useLiveData(
    subscriptionService.subscription.pro$.map(subscription =>
      subscription !== null ? subscription?.plan : null
    )
  );

  const [isShowSwitch, setIsShowSwitch] = useState(false);
  const preventHovering = useLiveData(appSidebarService.preventHovering$);
  const supportsHover = useHoverSupport();

  const handleMouseEnter = useCallback(() => {
    setIsShowSwitch(true);
    if (preventHovering || leftSidebarOpen) {
      return;
    }
    appSidebarService.setHovering(true);
  }, [appSidebarService, leftSidebarOpen, preventHovering]);

  const onClickUpgrade = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'plans',
    });
  }, [workspaceDialogService]);

  // For touch devices, use onClick to trigger the same behavior
  const handleClick = useCallback(() => {
    if (!supportsHover) {
      setIsShowSwitch(prev => {
        const newState = !prev;

        if (!preventHovering && !leftSidebarOpen) {
          appSidebarService.setHovering(newState);
        }
        return newState;
      });
    }
  }, [supportsHover, appSidebarService, leftSidebarOpen, preventHovering]);

  const t = useI18n();

  return (
    <div
      className={`${simpleSidebarStyle} ${isHomePage ? needRightBorder : ''}`}
    >
      <div className={simpleSidebarHeader}>
        <Tooltip
          content={
            isShowSwitch
              ? t['com.affine.rootAppSidebar.resize-handle.tooltip.sidebar']()
              : ''
          }
          side="right"
          align="center"
        >
          <div
            onMouseEnter={supportsHover ? handleMouseEnter : undefined}
            onMouseLeave={
              supportsHover ? () => setIsShowSwitch(false) : undefined
            }
            onClick={!supportsHover ? handleClick : undefined}
            className={simpleSidebarHeaderIconWrapper}
          >
            {!isShowSwitch ? (
              <IconFont name="mymuset" className={simpleSidebarHeaderIcon} />
            ) : (
              <SidebarSwitch show={!leftSidebarOpen} />
            )}
          </div>
        </Tooltip>
        <AddPageButton />
        <HomePageButton />
      </div>
      <div className={simpleSidebarBottom}>
        {loginStatus === 'authenticated' ? (
          <>
            {!plan && (
              <div
                className={simpleSidebarBottomAiFree}
                onClick={onClickUpgrade}
              >
                <div className={simpleSidebarBottomAiFreeTitle}>
                  {t['com.affine.app-simple-sidebar.ai-left']()}
                </div>
                <div className={simpleSidebarBottomAiFreeValue}>
                  {copilotActionLeft}
                </div>
                <div className={simpleSidebarBottomAiFreeButton}>
                  <span className={simpleSidebarBottomAiFreeButtonText}>
                    {t['com.affine.app-simple-sidebar.upgrade-button']()}
                  </span>
                </div>
              </div>
            )}
            <Menu items={<AccountMenu />}>
              <AvatarRoot>
                <AvatarImage
                  className={simpleSidebarBottomAvatar}
                  src={account?.avatar || ''}
                />
                <AvatarFallback>
                  <img
                    src="https://static.chatxbai.com/avatar/default/default-head.jpeg"
                    alt="avatar"
                    className={simpleSidebarBottomAvatar}
                  />
                </AvatarFallback>
              </AvatarRoot>
            </Menu>
          </>
        ) : (
          <div className={simpleSidebarBottomLogin}>
            <IconFont
              name="user"
              size={24}
              className={simpleSidebarBottomIcon}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export function FallbackHeader() {
  return (
    <div className={styles.fallbackHeader}>
      <FallbackHeaderSkeleton />
    </div>
  );
}

export function FallbackHeaderWithWorkspaceNavigator() {
  // if navigate is not defined, it is rendered outside of router
  // WorkspaceNavigator requires navigate context
  // todo: refactor
  const navigate = useContext(NavigateContext);

  const currentWorkspace = useServiceOptional(WorkspaceService);
  return (
    <div className={styles.fallbackHeader}>
      {currentWorkspace && navigate ? null : (
        // <WorkspaceNavigator showSyncStatus showEnableCloudButton dense />
        <FallbackHeaderSkeleton />
      )}
    </div>
  );
}

export function FallbackHeaderSkeleton() {
  return (
    <>
      <Skeleton variant="rectangular" width={32} height={32} />
      <Skeleton variant="rectangular" width={150} height={32} flex={1} />
      <Skeleton variant="circular" width={25} height={25} />
    </>
  );
}

const randomWidth = () => {
  return Math.floor(Math.random() * 200) + 100;
};

const RandomBar = ({ className }: { className?: string }) => {
  const width = useMemo(() => randomWidth(), []);
  return (
    <Skeleton
      variant="rectangular"
      width={width}
      height={16}
      className={className}
    />
  );
};

const RandomBars = ({ count, header }: { count: number; header?: boolean }) => {
  return (
    <div className={styles.fallbackGroupItems}>
      {header ? (
        <Skeleton
          className={styles.fallbackItemHeader}
          variant="rectangular"
          width={50}
          height={16}
        />
      ) : null}
      {Array.from({ length: count }).map((_, index) => (
        // oxlint-disable-next-line eslint-plugin-react(no-array-index-key)
        <RandomBar key={index} />
      ))}
    </div>
  );
};

const FallbackBody = () => {
  return (
    <div className={styles.fallbackBody}>
      <RandomBars count={3} />
      <RandomBars count={4} header />
      <RandomBars count={4} header />
      <RandomBars count={3} header />
    </div>
  );
};

export const AppSidebarFallback = (): ReactElement | null => {
  const appSidebarService = useService(AppSidebarService).sidebar;
  const width = useLiveData(appSidebarService.width$);
  const { appSettings } = useAppSettingHelper();
  const clientBorder = appSettings.clientBorder;

  return (
    <div
      style={{ width }}
      className={navWrapperStyle}
      data-has-border={!BUILD_CONFIG.isElectron && !clientBorder}
      data-open="true"
    >
      <nav className={navStyle}>
        {!BUILD_CONFIG.isElectron ? <div className={navHeaderStyle} /> : null}
        <div className={navBodyStyle}>
          <div className={styles.fallback}>
            <FallbackHeaderWithWorkspaceNavigator />
            <FallbackBody />
          </div>
        </div>
      </nav>
    </div>
  );
};

/**
 * NOTE(@forehalo): this is a copy of [AppSidebarFallback] without [WorkspaceNavigator] which will introduce a lot useless dependencies for shell(tab bar)
 */
export const ShellAppSidebarFallback = () => {
  const appSidebarService = useService(AppSidebarService).sidebar;
  const width = useLiveData(appSidebarService.width$);
  const { appSettings } = useAppSettingHelper();
  const clientBorder = appSettings.clientBorder;

  return (
    <div
      style={{ width }}
      className={navWrapperStyle}
      data-has-border={!BUILD_CONFIG.isElectron && !clientBorder}
      data-open="true"
    >
      <nav className={navStyle}>
        {!BUILD_CONFIG.isElectron ? <div className={navHeaderStyle} /> : null}
        <div className={navBodyStyle}>
          <div className={styles.fallback}>
            <FallbackHeader />
            <FallbackBody />
          </div>
        </div>
      </nav>
    </div>
  );
};

export * from './add-page-button';
export * from './app-download-button';
export * from './app-updater-button';
export * from './category-divider';
export * from './index.css';
export * from './menu-item';
export * from './open-in-app-card';
export * from './quick-search-input';
export * from './sidebar-containers';
export * from './sidebar-header';
